/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useOutletContext } from "react-router-dom";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { EmailAuthProvider, reauthenticateWithCredential, signOut } from "firebase/auth";

import { auth, db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import "../styles/parent/ParentProfile.css";

import {
  requestPushPermission,
  registerDeviceForPush,
} from "../services/notificationService";
import { hapticTap, hapticSelection, hapticSuccess } from "../utils/haptics";
import {
  clearCredentials,
  getBiometricAvailability,
  getStoredCredentials,
  isBiometricEnabled,
  storeCredentials,
  verifyBiometric,
} from "../utils/biometricAuth";

const NOTIFICATION_PREF_PREFIX = "pp_parent_notifications_enabled_";

function readLocalNotificationPreference(uid) {
  if (!uid) return null;
  try {
    const value = localStorage.getItem(`${NOTIFICATION_PREF_PREFIX}${uid}`);
    if (value === "true") return true;
    if (value === "false") return false;
  } catch {}
  return null;
}

function writeLocalNotificationPreference(uid, enabled) {
  if (!uid) return;
  try {
    localStorage.setItem(`${NOTIFICATION_PREF_PREFIX}${uid}`, enabled ? "true" : "false");
  } catch {}
}

export default function ParentProfile() {
  const navigate = useNavigate();
  const { user, profile, logout } = useAuth();
  const outlet = useOutletContext() || {};
  const unreadCount = outlet.unreadCount || 0;
  const schoolId = outlet.schoolId || "";
  const fallbackSchoolName = outlet.schoolName || "";

  const [notifications, setNotifications] = useState(false);
  const [biometric, setBiometric] = useState(false);
  const [notifBusy, setNotifBusy] = useState(false);
  const [biometricBusy, setBiometricBusy] = useState(false);
  const [logoutBusy, setLogoutBusy] = useState(false);
  const [showBiometricSetup, setShowBiometricSetup] = useState(false);
  const [setupPassword, setSetupPassword] = useState("");
  const [setupError, setSetupError] = useState("");
  const [schoolSettings, setSchoolSettings] = useState(null);
  const [schoolSettingsLoading, setSchoolSettingsLoading] = useState(false);

  const children = Array.isArray(profile?.children) ? profile.children : [];

  const firstName = useMemo(() => {
    const raw =
      profile?.displayName ||
      profile?.name ||
      profile?.fullName ||
      "Parent";

    return String(raw).split(" ")[0];
  }, [profile]);

  useEffect(() => {
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.pushEnabled, profile?.biometricEnabled, profile?.notificationPrefs]);


  useEffect(() => {
    let cancelled = false;

    async function loadSchoolSettings() {
      if (!schoolId) {
        setSchoolSettings(null);
        return;
      }

      setSchoolSettingsLoading(true);

      try {
        const settingsRef = doc(db, "schools", schoolId, "settings", "school");
        const settingsSnap = await getDoc(settingsRef);

        if (cancelled) return;

        if (settingsSnap.exists()) {
          const data = settingsSnap.data() || {};

          setSchoolSettings({
            schoolName:
              data.schoolName ||
              data.name ||
              fallbackSchoolName ||
              "Your school",
            email: data.email || "",
            phone: data.phone || "",
            address: data.address || "",
            supportHours: data.supportHours || "",
            timezone: data.timezone || "Europe/London",
            attendanceCutoff: data.attendanceCutoff || "09:00",
          });
        } else {
          setSchoolSettings({
            schoolName: fallbackSchoolName || "Your school",
            email: "",
            phone: "",
            address: "",
            supportHours: "",
            timezone: "Europe/London",
            attendanceCutoff: "09:00",
          });
        }
      } catch (error) {
        console.error("Failed loading school settings for parent profile:", error);

        if (!cancelled) {
          setSchoolSettings({
            schoolName: fallbackSchoolName || "Your school",
            email: "",
            phone: "",
            address: "",
            supportHours: "",
            timezone: "Europe/London",
            attendanceCutoff: "09:00",
          });
        }
      } finally {
        if (!cancelled) {
          setSchoolSettingsLoading(false);
        }
      }
    }

    loadSchoolSettings();

    return () => {
      cancelled = true;
    };
  }, [schoolId, fallbackSchoolName]);

  async function loadSettings() {
    try {
      const localBiometric = await isBiometricEnabled();
      const savedCredentials = await getStoredCredentials();

      const biometricOn =
        profile?.biometricEnabled === true &&
        localBiometric === true &&
        Boolean(savedCredentials?.username && savedCredentials?.password);

      const savedNotificationPreference = readLocalNotificationPreference(user?.uid);

      const serverNotificationPreference =
        profile?.notificationPrefs?.parentExplicitEnabled === true ||
        profile?.notificationPrefs?.enabled === true ||
        profile?.pushEnabled === true ||
        profile?.pushTokenEnabled === true;

      const notificationsOn =
        savedNotificationPreference !== null
          ? savedNotificationPreference
          : serverNotificationPreference;

      setBiometric(Boolean(biometricOn));
      setNotifications(Boolean(notificationsOn));
    } catch (err) {
      console.error("Failed loading parent settings:", err);
      setBiometric(false);
      setNotifications(false);
    }
  }

  async function toggleNotifications() {
    if (!user?.uid || notifBusy || logoutBusy) return;

    setNotifBusy(true);
    await hapticSelection();

    const previous = notifications;
    const next = !previous;

    try {
      // Update immediately, then persist the exact intended state.
      setNotifications(next);
      writeLocalNotificationPreference(user.uid, next);

      await setDoc(
        doc(db, "users", user.uid),
        {
          notificationPrefs: {
            enabled: next,
            parentExplicitEnabled: next,
            updatedAt: serverTimestamp(),
          },
          pushEnabled: next,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      if (next) {
        try {
          const permission = await requestPushPermission();

          if (permission?.granted) {
            await registerDeviceForPush(user);
          }
        } catch (err) {
          console.warn("Push registration failed but preference was saved:", err);
        }
      }

      await hapticSuccess();
    } catch (err) {
      console.error("Failed toggling notifications:", err);
      setNotifications(previous);
      writeLocalNotificationPreference(user.uid, previous);
      alert("Notification setting could not be saved.");
    } finally {
      setNotifBusy(false);
    }
  }

  async function enableBiometric(passwordFromModal) {
    const email = String(user?.email || profile?.email || "").trim().toLowerCase();

    if (!user?.uid || !auth.currentUser || auth.currentUser.uid !== user.uid) {
      throw new Error("Please sign in again before enabling biometric lock.");
    }

    if (!email) {
      throw new Error("No school-registered email was found for this account.");
    }

    const password = String(passwordFromModal || "");

    if (!password) {
      throw new Error("Please enter your PresentPlus password.");
    }

    /*
      Important: do NOT call signInWithEmailAndPassword here.
      The parent is already signed in, so we reauthenticate the current Firebase
      user instead of replacing/disturbing the active auth session.
    */
    const credential = EmailAuthProvider.credential(email, password);
    await reauthenticateWithCredential(auth.currentUser, credential);

    /*
      Small stability delay:
      Firebase auth/profile state can still be settling after reauthentication.
      Waiting briefly before the native prompt avoids checking biometric while
      the app context is mid-transition.
    */
    await new Promise((resolve) => setTimeout(resolve, 250));

    const availability = await getBiometricAvailability();

    if (!availability?.isAvailable) {
      throw new Error(
        "Biometric unlock is not available on this device. Please make sure Face ID, Touch ID, or device passcode is enabled."
      );
    }

    await verifyBiometric({
      reason: "Enable PresentPlus Quick Unlock",
      title: "PresentPlus",
      subtitle: "Confirm it is you",
      description: "Use your phone biometric security to protect the parent app.",
      negativeButtonText: "Cancel",
    });

    /*
      Full login Face ID needs a Firebase login secret after the phone biometric
      check succeeds. Store it in native secure storage, not Firestore and not
      the NativeBiometric credential helper that previously caused keychain errors.
    */
    await storeCredentials(email, password);

    /*
      Firestore rules allow only biometricEnabled + updatedAt for this setting.
      Do not write biometricUpdatedAt here because the current rules reject it.
    */
    await setDoc(
      doc(db, "users", user.uid),
      {
        biometricEnabled: true,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    setBiometric(true);
    await hapticSuccess();

    return true;
  }

  async function toggleBiometric() {
    if (!user?.uid || biometricBusy || logoutBusy) return;

    await hapticSelection();

    if (!biometric) {
      setSetupPassword("");
      setSetupError("");
      setShowBiometricSetup(true);
      return;
    }

    setBiometricBusy(true);

    try {
      await clearCredentials();

      await setDoc(
        doc(db, "users", user.uid),
        {
          biometricEnabled: false,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      setBiometric(false);
      await hapticSuccess();
    } catch (err) {
      console.error("Failed disabling biometric:", err);
      alert("Biometric setting could not be saved.");
    } finally {
      setBiometricBusy(false);
    }
  }

  async function submitBiometricSetup(event) {
    event.preventDefault();

    if (biometricBusy || logoutBusy) return;

    setBiometricBusy(true);
    setSetupError("");

    try {
      await enableBiometric(setupPassword);

      setShowBiometricSetup(false);
      setSetupPassword("");
    } catch (err) {
      console.error("Failed enabling biometric:", err);

      try {
        await clearCredentials();
      } catch {}

      setBiometric(false);

      const rawMessage = String(err?.message || "");
      const friendlyMessage =
        rawMessage.toLowerCase().includes("wrong-password") ||
        rawMessage.toLowerCase().includes("invalid-credential")
          ? "The password entered is not correct for this parent account."
          : rawMessage.toLowerCase().includes("not available")
          ? "Biometric unlock is not available right now. Please make sure Face ID, Touch ID, or device passcode is enabled on this phone."
          : rawMessage ||
            "Quick Unlock could not be enabled. Please check your password and try again.";

      setSetupError(friendlyMessage);
    } finally {
      setBiometricBusy(false);
    }
  }

  function keepBiometricPasswordVisible(event) {
    const input = event?.currentTarget;

    const reveal = () => {
      try {
        input?.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      } catch {}
    };

    window.setTimeout(reveal, 120);
    window.setTimeout(reveal, 360);
  }

  function closeBiometricSetup() {
    if (biometricBusy) return;

    setShowBiometricSetup(false);
    setSetupPassword("");
    setSetupError("");
  }

  function openTelephone(phone) {
    const cleaned = String(phone || "").replace(/[^+\d]/g, "");
    if (!cleaned) return;
    window.location.href = `tel:${cleaned}`;
  }

  function openEmail(email) {
    const value = String(email || "").trim();
    if (!value) return;
    window.location.href = `mailto:${value}`;
  }

  function openAddress(address) {
    const value = String(address || "").trim();
    if (!value) return;
    window.open(
      `https://maps.apple.com/?q=${encodeURIComponent(value)}`,
      "_blank",
      "noopener,noreferrer"
    );
  }

  async function handleLogout() {
    if (logoutBusy) return;

    setLogoutBusy(true);
    await hapticTap();

    try {
      try {
        sessionStorage.clear();
        localStorage.removeItem("pp:lastChild");
      } catch {}

      if (typeof logout === "function") {
        await logout();
      } else {
        await signOut(auth);
      }

      navigate("/parent-login", { replace: true });
    } catch (err) {
      console.error("Failed signing out:", err);
      alert("Could not log out. Please try again.");
      setLogoutBusy(false);
    }
  }

  return (
    <div className={`pp-profile-page ${showBiometricSetup ? "pp-modal-open" : ""}`}>
      <section className="pp-profile-hero">
        <div>
          <p className="pp-profile-eyebrow">Parent profile</p>
          <h1 className="pp-profile-title">Hi, {firstName}</h1>
          <p className="pp-profile-copy">
            Manage your security, alerts, and connected parent settings.
          </p>
        </div>

        <div className="pp-profile-badge">
          {unreadCount ? `${unreadCount} unread` : "Up to date"}
        </div>
      </section>

      <section className="pp-profile-section">
        <div className="pp-profile-card polished">
          <button
            type="button"
            className="pp-profile-row clickable polished"
            onClick={toggleNotifications}
            disabled={notifBusy || logoutBusy}
          >
            <div className="pp-profile-row-copy">
              <div className="pp-profile-label">Notifications</div>
              <div className="pp-profile-sublabel">
                Notifications are specific to each linked child profile.
              </div>
            </div>

            <div className={`pp-toggle ${notifications ? "on" : ""}`}>
              <span className="pp-toggle-knob" />
            </div>
          </button>

          <button
            type="button"
            className="pp-profile-row clickable polished"
            onClick={toggleBiometric}
            disabled={biometricBusy || logoutBusy}
          >
            <div className="pp-profile-row-copy">
              <div className="pp-profile-label">Biometric lock</div>
              <div className="pp-profile-sublabel">
                {biometric
                  ? "Quick Unlock is ready on this device."
                  : "Enable once from Profile. Login will then use one biometric check."}
              </div>
            </div>

            <div className={`pp-toggle ${biometric ? "on" : ""}`}>
              <span className="pp-toggle-knob" />
            </div>
          </button>
        </div>
      </section>

      <section className="pp-profile-section">
        <div className="pp-profile-card polished">
          <div className="pp-profile-row polished">
            <div className="pp-profile-row-copy">
              <div className="pp-profile-label">
                Linked children
              </div>
              <div className="pp-profile-sublabel">
                {children.length
                  ? `${children.length} linked child${
                      children.length === 1 ? "" : "ren"
                    }`
                  : "No linked children yet"}
              </div>
            </div>

            <div className="pp-profile-value">{children.length}</div>
          </div>

          <div className="pp-profile-row polished">
            <div className="pp-profile-row-copy">
              <div className="pp-profile-label">
                School-registered email
              </div>
              <div className="pp-profile-sublabel">
                {user?.email || profile?.email || "No email available"}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pp-profile-section">
        <div className="pp-profile-section-heading">
          <div>
            <p className="pp-profile-school-eyebrow">School Contact & Info</p>
            <h2>{schoolSettings?.schoolName || fallbackSchoolName || "Your school"}</h2>
          </div>
          <span>Live from admin settings</span>
        </div>

        <div className="pp-profile-card polished pp-profile-school-card">
          <div className="pp-profile-school-highlight">
            <div className="pp-profile-school-icon" aria-hidden="true">🕘</div>
            <div>
              <div className="pp-profile-label">Attendance cut-off</div>
              <div className="pp-profile-school-main-value">
                {schoolSettingsLoading
                  ? "Loading…"
                  : schoolSettings?.attendanceCutoff || "09:00"}
              </div>
              <div className="pp-profile-sublabel">
                Attendance should be recorded or reported before this time.
              </div>
            </div>
          </div>

          <div className="pp-profile-school-grid">
            <button
              type="button"
              className="pp-profile-school-item clickable"
              onClick={() => openTelephone(schoolSettings?.phone)}
              disabled={!schoolSettings?.phone}
            >
              <span className="pp-profile-school-item-icon" aria-hidden="true">☎</span>
              <span>
                <strong>Phone</strong>
                <small>{schoolSettings?.phone || "Not provided by school"}</small>
              </span>
            </button>

            <button
              type="button"
              className="pp-profile-school-item clickable"
              onClick={() => openEmail(schoolSettings?.email)}
              disabled={!schoolSettings?.email}
            >
              <span className="pp-profile-school-item-icon" aria-hidden="true">✉</span>
              <span>
                <strong>Email</strong>
                <small>{schoolSettings?.email || "Not provided by school"}</small>
              </span>
            </button>

            <button
              type="button"
              className="pp-profile-school-item clickable"
              onClick={() => openAddress(schoolSettings?.address)}
              disabled={!schoolSettings?.address}
            >
              <span className="pp-profile-school-item-icon" aria-hidden="true">⌖</span>
              <span>
                <strong>Address</strong>
                <small>{schoolSettings?.address || "Not provided by school"}</small>
              </span>
            </button>

            <div className="pp-profile-school-item">
              <span className="pp-profile-school-item-icon" aria-hidden="true">◷</span>
              <span>
                <strong>Support hours</strong>
                <small>{schoolSettings?.supportHours || "Not provided by school"}</small>
              </span>
            </div>

            <div className="pp-profile-school-item">
              <span className="pp-profile-school-item-icon" aria-hidden="true">◉</span>
              <span>
                <strong>Timezone</strong>
                <small>{schoolSettings?.timezone || "Europe/London"}</small>
              </span>
            </div>
          </div>
        </div>
      </section>

      <section className="pp-profile-section">
        <button
          type="button"
          className="pp-profile-logout-btn"
          onClick={handleLogout}
          disabled={logoutBusy}
        >
          {logoutBusy ? "Logging out…" : "Log out"}
        </button>
      </section>

      <style>{`
        .pp-profile-section-heading {
          margin: 0 2px 12px;
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          gap: 16px;
        }

        .pp-profile-section-heading h2 {
          margin: 0;
          color: #173566;
          font-size: 1.22rem;
          line-height: 1.15;
          letter-spacing: -0.03em;
          font-weight: 950;
        }

        .pp-profile-school-eyebrow {
          margin: 0 0 5px;
          color: #7388ad;
          font-size: 0.68rem;
          font-weight: 950;
          letter-spacing: 0.13em;
          text-transform: uppercase;
        }

        .pp-profile-section-heading > span {
          flex: 0 0 auto;
          color: #2d6df2;
          font-size: 0.7rem;
          font-weight: 900;
        }

        .pp-profile-school-card {
          overflow: hidden;
        }

        .pp-profile-school-highlight {
          padding: 18px;
          display: grid;
          grid-template-columns: 48px minmax(0, 1fr);
          gap: 14px;
          align-items: center;
          background:
            radial-gradient(circle at top right, rgba(45,109,242,0.12), transparent 38%),
            #f4f8ff;
          border-bottom: 1px solid #e1eaf8;
        }

        .pp-profile-school-icon,
        .pp-profile-school-item-icon {
          display: grid;
          place-items: center;
          color: #2d6df2;
          background: #e8f1ff;
        }

        .pp-profile-school-icon {
          width: 48px;
          height: 48px;
          border-radius: 16px;
          font-size: 1.2rem;
        }

        .pp-profile-school-main-value {
          margin: 3px 0 4px;
          color: #1557d7;
          font-size: 1.65rem;
          line-height: 1;
          font-weight: 950;
          letter-spacing: -0.04em;
        }

        .pp-profile-school-grid {
          display: grid;
          grid-template-columns: 1fr;
        }

        .pp-profile-school-item {
          width: 100%;
          min-width: 0;
          min-height: 76px;
          padding: 14px 16px;
          border: 0;
          border-bottom: 1px solid #edf1f8;
          display: grid;
          grid-template-columns: 42px minmax(0, 1fr);
          gap: 13px;
          align-items: center;
          color: inherit;
          background: #ffffff;
          text-align: left;
          font-family: inherit;
        }

        .pp-profile-school-item:last-child {
          border-bottom: 0;
        }

        .pp-profile-school-item.clickable:not(:disabled) {
          cursor: pointer;
        }

        .pp-profile-school-item.clickable:not(:disabled):active {
          background: #f6f9ff;
        }

        .pp-profile-school-item:disabled {
          opacity: 1;
        }

        .pp-profile-school-item-icon {
          width: 42px;
          height: 42px;
          border-radius: 14px;
          font-size: 1rem;
          font-weight: 950;
        }

        .pp-profile-school-item > span:last-child {
          min-width: 0;
          display: grid;
          gap: 4px;
        }

        .pp-profile-school-item strong {
          color: #173566;
          font-size: 0.88rem;
          font-weight: 950;
        }

        .pp-profile-school-item small {
          color: #7186aa;
          font-size: 0.79rem;
          line-height: 1.4;
          font-weight: 750;
          overflow-wrap: anywhere;
        }

        @media (min-width: 700px) {
          .pp-profile-school-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }

          .pp-profile-school-item {
            border-right: 1px solid #edf1f8;
          }

          .pp-profile-school-item:nth-child(2n) {
            border-right: 0;
          }
        }
.pp-biometric-modal {
            margin-top: 0 !important;
            margin-bottom: 0 !important;
          }
        }
      `}</style>

      {showBiometricSetup
        ? createPortal(
            <div className="pp-biometric-modal-backdrop" role="presentation">
              <form
                className="pp-biometric-modal"
                onSubmit={submitBiometricSetup}
                onClick={(event) => event.stopPropagation()}
                onPointerDown={(event) => event.stopPropagation()}
                onTouchStart={(event) => event.stopPropagation()}
              >
                <div className="pp-biometric-modal-icon">🔐</div>

                <p className="pp-biometric-modal-eyebrow">PresentPlus security</p>
                <h2>Enable Quick Unlock</h2>
                <p className="pp-biometric-modal-copy">
                  Confirm your PresentPlus password once. After that, you can sign in
                  securely using Face ID or Touch ID on this device.
                </p>

                <label className="pp-biometric-password-label">
                  <span>Password</span>
                  <input
                    type="password"
                    value={setupPassword}
                    onChange={(event) => setSetupPassword(event.target.value)}
                    onFocus={keepBiometricPasswordVisible}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    disabled={biometricBusy}
                  />
                </label>

                {setupError ? (
                  <div className="pp-biometric-modal-error">{setupError}</div>
                ) : null}

                <div className="pp-biometric-modal-actions">
                  <button
                    type="button"
                    className="secondary"
                    onClick={closeBiometricSetup}
                    disabled={biometricBusy}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="primary"
                    disabled={biometricBusy || !setupPassword}
                  >
                    {biometricBusy ? "Checking…" : "Continue"}
                  </button>
                </div>
              </form>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
